import React from "react";
import {Button, DatePicker, Select} from "antd/lib";
import {ArrowLeftOutlined, CalendarOutlined} from "@ant-design/icons";
import MonthlyAttendanceChart from "./attendance-month-trend-chart";
import dayjs from "dayjs";
import {IAttendanceStatsView} from "../types/attendance.types";
import attendance from "../../../../resources/attendance";
import "../../../../styles/component/attendance-stats.scss";
import PageHeader from "../../../common/page-header/page-header";
import StatCards, {StatCardColor} from "../../../common/stat-cards/stat-cards";
import {CalendarOff, CalendarX, CircleCheck, CircleX, Clock4} from "lucide-react";
import LoadingSkeleton from "../../../common/loading-skeleton/loading-skeleton";

const AttendanceStatsView: React.FC<IAttendanceStatsView> = (props) => {
    const resource = attendance;
    const headerContent = props.isAdmin ?
        resource.attendanceStatHeaderText.adminEmployee
        :
        resource.attendanceStatHeaderText.employeeSelf;

    //render employeeName if admin view employee stats
    const empName = props.isAdmin ? props.selectedEmployeeName : '';

    const statsCardData = [
        {
            key: 'presentDays',
            label: 'Present Days',
            value: props.monthSummary?.presentDays,
            icon: CircleCheck,
            color: 'green' as StatCardColor
        }, {
            key: 'absentDays',
            label: 'Absent Days',
            value: props.monthSummary?.absentDays,
            icon: CircleX,
            color: 'red' as StatCardColor
        }, {
            key: 'leaveDays',
            label: 'Leave days',
            value: props.monthSummary?.leaveDays,
            icon: CalendarOff,
            color: 'orange' as StatCardColor
        }, {
            key: 'holidays',
            label: 'Holidays',
            value: props.monthSummary?.holidays,
            icon: Clock4,
            color: 'blue' as StatCardColor
        },
        {
            key: 'weekends',
            label: 'Weekend Days',
            value: props.monthSummary?.weekends,
            icon: CalendarX,
            color: 'brown' as StatCardColor
        },
    ];

    const getRateClass = (rate: number) => {
        if (rate === 0) {
            return "attendance-rate-month--empty";
        }

        if (rate >= 70) {
            return "attendance-rate-month--high";
        }

        if (rate >= 50) {
            return "attendance-rate-month--medium-high";
        }

        if (rate >= 30) {
            return "attendance-rate-month--medium";
        }

        if (rate > 0) {
            return "attendance-rate-month--low";
        }

        return "attendance-rate-month--empty";
    };

    return (
        <div className="attendance-stats">
            <PageHeader
                title={`${headerContent.mainHeading} ${empName}`}
                description={headerContent.description}
                actions={<Button
                    icon={<ArrowLeftOutlined/>}
                    className={'go-to-btn'}
                    onClick={() => props.handleNavigationToRecord(props.employeeId)}
                >
                    Go back to record
                </Button>}
            />

            <div className="attendance-stats__filters">
                <div className="attendance-stats__filters-section">
                    <h3 className="attendance-stats__filters-title">
                        <CalendarOutlined className="attendance-stats__filters-icon"/>
                        Time Period
                    </h3>

                    <div className="attendance-stats__filters-controls">
                        <div className="attendance-stats__filter attendance-stats__filter--year">
                            <span className="attendance-stats__filter-label">Year</span>
                            <DatePicker
                                onChange={props.handleYearChange}
                                picker="year"
                                value={props.filters.date}
                                disabledDate={(current) => current && current.year() > dayjs().year()}
                            />
                        </div>

                        <div className="attendance-stats__filter attendance-stats__filter--month">
                            <span className="attendance-stats__filter-label">Month</span>
                            <Select
                                options={props.months}
                                placeholder="Select Month"
                                value={props.filters.month}
                                onChange={props.handleMonthValueChange}
                            />
                        </div>

                        <Button
                            className="attendance-stats__filter-button"
                            onClick={() => props.handleQuickMonth("curr")}
                        >
                            This Month
                        </Button>
                        <Button
                            className="attendance-stats__filter-button"
                            onClick={() => props.handleQuickMonth("last")}
                        >
                            Last Month
                        </Button>
                    </div>
                </div>

                <div className="attendance-stats__filters-summary">
                    <span className="attendance-stats__summary-label">Showing data for</span>
                    <span className="attendance-stats__summary-value">
                          {props.months.find((i) => i.value === props.filters.month)?.label ??
                          "All Months"}{" "}
                        {props.filters.year}
                 </span>
                </div>
            </div>

            <a className="attendance-stats__report-link" aria-label="Generate Analytics Report"/>
            <StatCards data={statsCardData} size={"small"} loading={props.isLoading}/>

            <div className={'attendance-rate-card-section'}>
                <div className="attendance-rate-card-title">
                    Attendance rate by month
                </div>
                <LoadingSkeleton loading={props.isLoading}>
                    <div className={'attendance-rate-months'}>
                        {props.summary?.months?.map((item) => {
                            const attendanceRate = item.workingDays
                                ? Number(((item.presentDays / item.workingDays) * 100).toFixed(1))
                                : 0;

                            return (
                                <div key={item.month} className={"attendance-rate-month"}>
                                    <div className={`attendance-rate-cards ${getRateClass(attendanceRate)}`}>
                                        <div className={'attendance-rate-percentage'}>
                                            {attendanceRate}%
                                        </div>
                                        <div>
                                            {item.month.substring(0, 3)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </LoadingSkeleton>
            </div>

            <MonthlyAttendanceChart summary={props.summary?.months} year={props.filters.year}
                                    loading={props.isLoading}/>
        </div>
    );
};

export default AttendanceStatsView;